import TireTable from './tire-table';
import TireWarrantyFileInputs from './tire-warranty-file-inputs';

export enum TireWarrantyStatus {
  New = '',
  Draft = '00',
  Error = '10',
  Reviewed = '20',
  Rejected = '30',
  CreditNoteIssued = '70',
  CreditNoteIssuedGoodwill = '75',
  Cancelled = '99'
}

export default class TireWarrantyForm {

  private readonly submitButton: JQuery;
  private readonly saveButton: JQuery;
  private readonly saveFormEndpoint: string;
  private readonly tireTable: TireTable;

  // Form Inputs
  private readonly reference: JQuery;
  private readonly reason: JQuery;
  private readonly status: JQuery;
  private readonly disassemblyDate: JQuery;
  private readonly tireRowSelector: string = '.js-tireRow';

  private readonly tireErrorMessagesContainerSelector: string = '.js-newTireErrorMessage';

  private readonly tireWarrantyFileInputs: TireWarrantyFileInputs;

  private preventPageLeave = false;

  constructor(private form: JQuery) {
    this.submitButton = form.find('.js-submit');
    this.saveButton = form.find('.js-saveForLater');
    this.saveFormEndpoint = form.data('save-endpoint') as string;

    this.reference = form.find('.js-reference');
    this.reason = form.find('.js-reason');
    this.status = form.find('.js-status');

    this.disassemblyDate = form.find('.js-disassemblyDate');
    this.disassemblyDate.datepicker('option', { minDate: '-30d', maxDate: '0' });

    this.tireTable = new TireTable(form.find('.js-tireTable'), this.disassemblyDate);

    this.tireWarrantyFileInputs = new TireWarrantyFileInputs(this.form);

    this.bindChangeReason();
    this.bindSaveButton();
    this.bindButtonsToValidation();
    this.bindFileValidation();
    this.bindPreventPageLeave();
    this.bindFormSubmit();

    this.prepareForm();
    this.enableSaveIfValid();
    this.enableSubmitIfValid();

    this.bindSubmitButton();

    if (this.getStatus() === TireWarrantyStatus.Error) {
      this.validate();
    }

  }

  private bindSubmitButton(): void {
    this.submitButton.on('click', (e): void => {
      this.submitForm($(e.currentTarget));
    });
  }

  private prepareForm(): void {
    const status = this.getStatus();

    switch (status) {
      case TireWarrantyStatus.Draft:
      case TireWarrantyStatus.Error:
        return;
      default:
        this.disableForm();
    }

    if (status === TireWarrantyStatus.New) {
      this.reason.prop('disabled', false);
    }
  }

  disableForm(): void {
    this.form.find('input, select, textarea').prop('disabled', true);
  }

  enableForm(): void {
    this.form.find('input, select, textarea').prop('disabled', false);
  }

  private bindChangeReason(): void {
    this.reason.on('change', () => {
      this.enableForm();
    });
  }

  private bindSaveButton(): void {
    this.saveButton.click((e): void => {
      if (!this.isValidForSave()) {
        const requiredFileInputs = this.form.find('.js-fileInput[required]');
        this.reference.prop('required', true);
        requiredFileInputs.prop('required', false);
        this.reference.parsley().validate();
        this.reference.prop('required', false);
        e.preventDefault();
        // Prevent immediate re-validation with timeout
        setTimeout(() => {
          requiredFileInputs.prop('required', true);
        }, 0);
        return;
      }
      this.form.find('[required]:visible, #acceptedTermsAndConditions, #acceptedDataPrivacyPolicies').prop('required', false);

      this.submitForm($(e.currentTarget));
    });
  }

  private bindFileValidation(): void {
    this.submitButton.click((): void => this.validateFileInputs());
    this.saveButton.click((): void => this.validateFileInputs());
    this.validateFileInputsOnRemove();
  }

  private bindButtonsToValidation() {
    this.form.on('change', (e): void => {
      $(e.target).parsley().validate();
      this.preventPageLeave = true;
      this.enableSubmitIfValid();
      this.enableSaveIfValid();
    });
  }

  private bindPreventPageLeave(): void {
    // eslint-disable-next-line consistent-return
    window.onbeforeunload = () => {
      if (this.reason.val() !== null && this.preventPageLeave) return true;
    };
  }

  private validateFileInputsOnRemove(): void {
    $(document).on('tireWarrantyFileRemoved', (): void => {
      this.enableSaveIfValid();
      this.enableSubmitIfValid();
    });
  }

  validate(): void {
    this.form.parsley().validate();
    this.enableSubmitIfValid();
  }

  isValid(): boolean {
    // eslint-disable-next-line eqeqeq
    return this.form.parsley().isValid() && $(this.tireRowSelector).length > 0 && $(this.tireErrorMessagesContainerSelector).html() == '';
  }

  isValidForSave(): boolean {
    return !!this.reference.val();
  }

  private enableSaveIfValid(): void {
    if (this.reason.val()) {
      this.enableSave();
    } else {
      this.disableSave();
    }
  }

  private enableSubmitIfValid(): void {
    if (this.isValid()) {
      this.enableSubmit();
    } else {
      this.disableSubmit();
    }
  }

  private validateFileInputs(): void {
    this.form.find('input[type=file]').each((i, input): void => {
      $(input).parsley().validate();
    });
  }

  disableSubmit(): void {
    this.submitButton.prop('disabled', true);
  }

  enableSubmit(): void {
    this.submitButton.prop('disabled', false);
  }

  disableSave(): void {
    this.saveButton.prop('disabled', true);
  }

  enableSave(): void {
    this.saveButton.prop('disabled', false);
  }

  private bindFormSubmit(): void {
    this.form.on('submit', (): void => {
      this.disableSave();
      this.disableSubmit();
      this.preventPageLeave = false;
    });
  }

  getStatus(): string {
    return this.status.val() as string || '';
  }

  private submitForm(trigger: JQuery): void {
    const formAction = trigger.attr('formaction') as string;
    this.form.attr('action', formAction);
    this.form.submit();
  }

}
